import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  Skeleton,
} from "@mui/material";
import { Edit, Delete, Visibility, FilterList, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { encryptId } from "../../Components/CryptoComponent";
import { errorNotify } from "../../Components/ToastComponent/ToastNotification";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));


const ListRole = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [loader, setLoader] = useState(true);
  const [data, setData] = useState([]);

  const getData = () => {
    axiosAPI
      ?.get(`/UserRole/list`)
      .then((res) => {
        setLoader(false);
        if (res?.data?.data) {
          setData(res?.data?.data);
        }
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const navigatetoAdd = () => navigate("/Roles/Add");
  const navigatetoEdit = (id) => {
    const encrypted = encodeURIComponent(encryptId(id));
    navigate(`/Roles/Edit/${encrypted}`);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, []);

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Search and Actions */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Role Management
        </Typography>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{
              fontFamily: "Geist, sans-serif",
              textTransform: "capitalize",
              fontSize: { xs: "12px", sm: "14px" },
              padding: { xs: "5px 10px", sm: "6px 12px" },
            }}
          >
            Filter
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            color="primary"
            onClick={navigatetoAdd}
            sx={{
              background: "#E75E1F",
              fontFamily: "Geist, sans-serif",
              textTransform: "capitalize",
              fontSize: { xs: "12px", sm: "14px" },
              padding: { xs: "5px 10px", sm: "6px 12px" },
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "90vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        {loader ? (
          // Render skeleton loader when loading
          <>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              style={{ marginTop: "10px" }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              style={{ marginTop: "10px" }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              style={{ marginTop: "10px" }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              style={{ marginTop: "10px" }}
            />
          </>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ width: "80px"}}>#</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell sx={{ width: "150px", textAlign: "center" }}>
                  Status
                </StyledTableCell>
                <StyledTableCell sx={{ width: "150px", textAlign: "center" }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loader
                ? Array.from({ length: rowsPerPage }).map((_, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>
                        <Skeleton variant="text" width="50px" />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Skeleton variant="text" width="150px" />
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ width: "150px", textAlign: "right" }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width={80}
                          height={30}
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ width: "150px", textAlign: "right" }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width={80}
                          height={30}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                : // Render actual data when available
                  data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((parent, index) => (
                      <StyledTableRow
                        key={parent.id}
                        sx={{ border: "2px solid transparent" }}
                      >
                        <StyledTableCell
                          sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                        >
                          {index + 1}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "12px", sm: "14px" },
                              fontFamily: "Geist, sans-serif",
                            }}
                          >
                            {parent?.RL_Name ? parent?.RL_Name : "-"}
                          </Typography>
                        </StyledTableCell>

                        {/* Status Column - Rightmost */}
                        <StyledTableCell
                          sx={{ width: "150px", textAlign: "right" }}
                        >
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Chip
                              label={
                                parent.RL_Status == "1" ? "Active" : "Inactive"
                              }
                              color={
                                parent.RL_Status == "1" ? "success" : "error"
                              }
                              variant="outlined"
                              sx={{
                                fontSize: { xs: "10px", sm: "12px" },
                                width: "80px",
                                fontFamily: "Geist, sans-serif",
                                fontWeight: "600",
                              }}
                            />
                          </Box>
                        </StyledTableCell>

                        {/* Actions Column - Rightmost */}
                        <StyledTableCell
                          sx={{ width: "150px", textAlign: "right" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: "5px",
                            }}
                          >
                            <IconButton
                              color="warning"
                              onClick={() => {
                                navigatetoEdit(parent?.RL_Id);
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default ListRole;
